import { useMemo } from "react";
import dayjs from "dayjs";

import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";

import { getCalendarEventColor } from "./NodiantCalendar";

export default function CalendarListView({
    calendars = [],
    events = [],
    handleEventClick,
}) {
    const calendarByKey = useMemo(
        () =>
            new Map(
                calendars.map((calendar) => [calendar.key, calendar.name]),
            ),
        [calendars],
    );

    const dates = useMemo(() => {
        const eventsByDate = new Map();

        events.forEach((event) => {
            const dateKey = dayjs(event.date).format("YYYY-MM-DD");
            const existingDate = eventsByDate.get(dateKey);

            if (existingDate) {
                existingDate.events.push(event);
            } else {
                eventsByDate.set(dateKey, {
                    date: event.date,
                    events: [event],
                });
            }
        });

        return Array.from(eventsByDate.values())
            .sort((a, b) =>
                dayjs(a.date).isAfter(dayjs(b.date)) ? 1 : -1,
            )
            .map((date) => ({
                ...date,
                events: date.events.sort((a, b) =>
                    a.name.localeCompare(b.name),
                ),
            }));
    }, [events]);

    if (dates.length === 0) {
        return (
            <Box
                sx={{
                    minHeight: 360,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 3,
                    textAlign: "center",
                    color: "text.secondary",
                }}
            >
                <EventBusyOutlinedIcon
                    sx={{ mb: 1.25, fontSize: 44, opacity: 0.4 }}
                />
                <Typography
                    variant="subtitle1"
                    sx={{ color: "text.primary", fontWeight: 800 }}
                >
                    No events to show
                </Typography>
                <Typography variant="body2">
                    Select another calendar or create a new event.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {dates.map((date) => (
                <Accordion
                    key={dayjs(date.date).format("YYYY-MM-DD")}
                    defaultExpanded
                    disableGutters
                    elevation={0}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "12px !important",
                        overflow: "hidden",
                        backgroundColor: "background.paper",
                        "&:before": { display: "none" },
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            px: { xs: 2, sm: 2.5 },
                            minHeight: 64,
                            "& .MuiAccordionSummary-content": {
                                alignItems: "center",
                                gap: 1,
                            },
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 800 }}
                            >
                                {dayjs(date.date).format(
                                    "dddd, D MMMM YYYY",
                                )}
                            </Typography>
                        </Box>
                        <Chip
                            label={date.events.length}
                            size="small"
                            sx={{
                                minWidth: 30,
                                backgroundColor: "rgba(0, 149, 61, 0.09)",
                                color: "primary.dark",
                            }}
                        />
                    </AccordionSummary>
                    <AccordionDetails
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            px: { xs: 1.5, sm: 2.5 },
                            pt: 0,
                            pb: 2,
                        }}
                    >
                        {date.events.map((event) => (
                            <ButtonBase
                                key={event.key}
                                onClick={
                                    handleEventClick &&
                                    handleEventClick(event)
                                }
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.25,
                                    width: "100%",
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    textAlign: "left",
                                    justifyContent: "flex-start",
                                    "&:hover": {
                                        borderColor:
                                            "rgba(0, 149, 61, 0.35)",
                                        boxShadow:
                                            "0 3px 12px rgba(20, 35, 25, 0.07)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 10,
                                        minHeight: 46,
                                        alignSelf: "stretch",
                                        borderRadius: 1,
                                        flexShrink: 0,
                                        backgroundColor:
                                            getCalendarEventColor(event.color),
                                    }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 800,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {event.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {calendarByKey.get(event.calendar) ||
                                            "Calendar"}
                                    </Typography>
                                </Box>
                            </ButtonBase>
                        ))}
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
}
